import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { sanitizeDocumentForFirestore } from '@/utils/firestore-sanitizer';

import { createAndStoreProjectWriteIntent } from './project-write-operation-factory';
import type { ProjectScope } from './project-scope';
import type { ProjectWriteOperation } from './write-operation';

type StructuredEntityKind = Extract<ProjectWriteOperation['kind'], 'patchEntity' | 'deleteEntity' | 'setField'>;

export type StructuredEntityWriteRegistration = {
    feature: ProjectWriteOperation['feature'];
    capability: string;
    conflictPrefix: string;
    allowedKinds: readonly StructuredEntityKind[];
    authErrorMessage: string;
};

export type StructuredEntityWriteInput = {
    scope: ProjectScope;
    registration: StructuredEntityWriteRegistration;
    entityId: string;
    kind: StructuredEntityKind;
    payload: unknown;
    actorId?: string;
};

function requireEntityId(entityId: string): string {
    const normalized = entityId.trim();
    if (!normalized) {
        throw new Error('Structured entity id is required');
    }
    if (normalized.includes('/')) {
        throw new Error('Structured entity id cannot contain "/"');
    }
    return normalized;
}

function sanitizeStructuredPayload(payload: unknown): unknown {
    if (typeof payload === 'object' && payload !== null && !Array.isArray(payload)) {
        return sanitizeDocumentForFirestore(payload);
    }
    return payload;
}

export async function enqueueStructuredEntityWrite(input: StructuredEntityWriteInput): Promise<void> {
    const actorId = input.actorId ?? getEffectiveUserId();
    if (!actorId) {
        throw new Error(input.registration.authErrorMessage);
    }

    if (!input.registration.allowedKinds.includes(input.kind)) {
        throw new Error(`${input.kind} is not allowed for ${input.registration.conflictPrefix} structured writes`);
    }

    const entityId = requireEntityId(input.entityId);

    await createAndStoreProjectWriteIntent({
        scope: input.scope,
        actorId,
        feature: input.registration.feature,
        capability: input.registration.capability,
        conflictUnit: `${input.registration.conflictPrefix}/${entityId}`,
        kind: input.kind,
        payload: sanitizeStructuredPayload(input.payload),
    });
}
